<template>
	<slot v-if="isLoading" name="loading">
		<div class="app-fetch-html-from-monolith__loading">
			<SuiLoadingCircle :aria-label="$t('loading')" size="medium" />
		</div>
	</slot>

	<slot v-else-if="isHTMLEmpty" name="empty" />

	<AppHTMLFromMonolith v-else :html="html" />
</template>

<script lang="ts" setup>
import { submitAjax, type ScoroAjaxResponse } from '@/lib/monolith';
import { SuiLoadingCircle } from '@developers/scoro-ui';
import { computed, ref, watch } from 'vue';
import AppHTMLFromMonolith from '../AppHTMLFromMonolith';

export type AppFetchHTMLFromMonolithProps = {
	url: string;
	params: Record<
		string,
		number | string | string[] | object | boolean | undefined
	>;
	getHtmlFromResponse: (
		response: ScoroAjaxResponse<Record<string, unknown>>
	) => string | undefined;
	checkIfHtmlIsEmpty?: (html: string) => boolean;
};

/**
 * This component fetches some HTML from the monolith, from the give URL, and
 * renders it into the DOM. Any time the URL or the params change, the content
 * is re-fetched.
 */
const props = defineProps<AppFetchHTMLFromMonolithProps>();

const emit = defineEmits<{
	(e: 'response', payload: { isEmpty: boolean; html: string }): void;
}>();

const html = ref<string>();

const isLoading = computed(() => {
	return html.value === undefined;
});

const isHTMLEmpty = computed(() => {
	if (!html.value) {
		return true;
	}

	if (!props.checkIfHtmlIsEmpty) {
		return false;
	}

	return props.checkIfHtmlIsEmpty(html.value);
});

const populateHTML = async () => {
	html.value = undefined;

	const response = await submitAjax(props.url, {
		path: document.querySelector<HTMLInputElement>(
			'#linkToCurrentTabWithActionAndObjectId'
		)?.value,
		...props.params,
	});
	if (response.status !== 'OK') {
		return;
	}

	html.value = props.getHtmlFromResponse(response);
};

watch([() => props.url, () => props.params], populateHTML, { immediate: true });

watch([html, isHTMLEmpty], () => {
	emit('response', { isEmpty: isHTMLEmpty.value, html: html.value || '' });
});
</script>

<style lang="scss" scoped>
.app-fetch-html-from-monolith {
	&__loading {
		display: flex;
		align-items: center;
		justify-content: center;
		padding-top: 3rem;
		padding-bottom: 3rem;
	}
}
</style>
